import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Typography,
  Chip,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Business,
  Phone,
  LocationOn,
} from '@mui/icons-material';
import api from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { exportToExcel, exportToPDF } from '../../utils/exportHelpers';
import { useLanguage } from '../../contexts/LanguageContext';
import { extractAndTranslateError, formatNumber, formatCurrency, formatPhoneNumber, formatStringWithNumbers } from '../../utils/i18nHelpers';

interface Amenity {
  id: number;
  name_en: string;
  name_ar: string;
  is_active: boolean;
}

interface Building {
  id: number;
  name: string;
  name_ar?: string;
  address: string;
  latitude?: number;
  longitude?: number;
  building_code: string;
  total_floors: number;
  total_flats: number;
  description?: string;
  amenities?: number[];
  haris_phone_number?: string;
  paci_number?: string;
  is_active: boolean;
  flats_count?: number;
  expenses_count?: number;
}

const Buildings: React.FC = () => {
  const { can } = useAuth();
  const { notifySuccess, notifyError } = useNotification();
  const { t, language } = useLanguage();
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [amenityDialogOpen, setAmenityDialogOpen] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState<Amenity | null>(null);
  const [editingBuilding, setEditingBuilding] = useState<Building | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submittingAmenity, setSubmittingAmenity] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    address: '',
    latitude: '',
    longitude: '',
    building_code: '',
    total_floors: 1,
    total_flats: 1,
    haris_phone_number: '',
    paci_number: '',
    description: '',
    amenities: [] as number[],
  });
  const [newAmenity, setNewAmenity] = useState({
    name_en: '',
    name_ar: '',
  });
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [mapSearchQuery, setMapSearchQuery] = useState('');
  const [mapPredictions, setMapPredictions] = useState<{ description: string; place_id?: string; lat?: number; lng?: number }[]>([]);
  const [selectedMapLocation, setSelectedMapLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isSearchingPlaces, setIsSearchingPlaces] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [autocompleteWidgetReady, setAutocompleteWidgetReady] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const autocompleteRef = useRef<any>(null);
  const autocompleteInputRef = useRef<HTMLInputElement | null>(null);
  const googleMapsLoading = useRef<Promise<void> | null>(null);

  useEffect(() => {
    fetchBuildings();
    fetchAmenities();
  }, []);

  // Inject CSS for Google Maps Autocomplete dropdown to ensure it's visible
  useEffect(() => {
    const styleId = 'google-maps-autocomplete-styles';
    if (document.getElementById(styleId)) return; // Already added

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .pac-container {
        z-index: 1400 !important;
        margin-top: 4px;
        border-radius: 4px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      }
      .pac-item {
        padding: 8px 12px;
        cursor: pointer;
      }
      .pac-item:hover {
        background-color: #f5f5f5;
      }
      .pac-item-selected {
        background-color: #e3f2fd;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  const fetchBuildings = async () => {
    try {
      const response = await api.get('/buildings');
      setBuildings(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching buildings:', error);
      setBuildings([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAmenities = async () => {
    try {
      const response = await api.get('/amenities');
      setAmenities(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching amenities:', error);
      setAmenities([]);
    }
  };

  const handleOpen = (building?: Building) => {
    if (building) {
      setEditingBuilding(building);
      // Ensure amenities are always an array of numbers
      const amenitiesArray = Array.isArray(building.amenities) 
        ? building.amenities.map(id => typeof id === 'string' ? parseInt(id, 10) : Number(id)).filter(id => !isNaN(id))
        : [];
      setFormData({
        name: building.name,
        name_ar: building.name_ar || '',
        address: building.address,
        latitude: building.latitude?.toString() || '',
        longitude: building.longitude?.toString() || '',
        building_code: building.building_code,
        total_floors: building.total_floors,
        total_flats: building.total_flats,
        haris_phone_number: building.haris_phone_number || '',
        paci_number: building.paci_number || '',
        description: building.description || '',
        amenities: amenitiesArray,
      });
    } else {
      setEditingBuilding(null);
      setFormData({
        name: '',
        name_ar: '',
        address: '',
        latitude: '',
        longitude: '',
        building_code: '',
        total_floors: 1,
        total_flats: 1,
        haris_phone_number: '',
        paci_number: '',
        description: '',
        amenities: [],
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingBuilding(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return; // Prevent double submission
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        amenities: formData.amenities.length > 0 ? formData.amenities : null,
      };
      if (editingBuilding) {
        await api.put(`/buildings/${editingBuilding.id}`, submitData);
        notifySuccess(t('buildings.messages.save.successUpdate'));
      } else {
        await api.post('/buildings', submitData);
        notifySuccess(t('buildings.messages.save.successCreate'));
      }
      fetchBuildings();
      handleClose();
    } catch (error: any) {
      console.error('Error saving building:', error);
      
      const errorMessage = extractAndTranslateError(error, language, t);
      notifyError(errorMessage || t('buildings.messages.save.errorDefault'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleMapPickerOpen = async () => {
    setMapError(null); // Clear any previous errors
    // Initialize with existing coordinates if available
    if (formData.latitude && formData.longitude) {
      setSelectedMapLocation({
        lat: parseFloat(formData.latitude),
        lng: parseFloat(formData.longitude),
      });
      setMapSearchQuery(`${formData.latitude}, ${formData.longitude}`);
    } else {
      // Try to get current location
      try {
        // First, try browser's geolocation API
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              setSelectedMapLocation({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              });
            },
            async (error) => {
              console.log('Browser geolocation failed, trying Google Geolocation API:', error);
              // Fallback to Google Geolocation API
              try {
                const googleApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyB1EiZd7oGK4fnmgbeOXyFmWOeCoJmKrAc';
                const response = await fetch(
                  `https://www.googleapis.com/geolocation/v1/geolocate?key=${googleApiKey}`,
                  {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      considerIp: true,
                    }),
                  }
                );
                const data = await response.json();
                if (data.location) {
                  setSelectedMapLocation({
                    lat: data.location.lat,
                    lng: data.location.lng,
                  });
                } else {
                  // Default to Kuwait City if all else fails
                  setSelectedMapLocation({
                    lat: 29.3759,
                    lng: 47.9774,
                  });
                }
              } catch (apiError) {
                console.error('Google Geolocation API failed:', apiError);
                // Default to Kuwait City
                setSelectedMapLocation({
                  lat: 29.3759,
                  lng: 47.9774,
                });
              }
            },
            {
              enableHighAccuracy: true,
              timeout: 5000,
              maximumAge: 0,
            }
          );
        } else {
          // Browser geolocation not available, try Google Geolocation API
          try {
            const googleApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyB1EiZd7oGK4fnmgbeOXyFmWOeCoJmKrAc';
            const response = await fetch(
              `https://www.googleapis.com/geolocation/v1/geolocate?key=${googleApiKey}`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  considerIp: true,
                }),
              }
            );
            const data = await response.json();
            if (data.location) {
              setSelectedMapLocation({
                lat: data.location.lat,
                lng: data.location.lng,
              });
            } else {
              // Default to Kuwait City
              setSelectedMapLocation({
                lat: 29.3759,
                lng: 47.9774,
              });
            }
          } catch (apiError) {
            console.error('Google Geolocation API failed:', apiError);
            // Default to Kuwait City
            setSelectedMapLocation({
              lat: 29.3759,
              lng: 47.9774,
            });
          }
        }
      } catch (error) {
        console.error('Error getting current location:', error);
        // Default to Kuwait City
        setSelectedMapLocation({
          lat: 29.3759,
          lng: 47.9774,
        });
      }
      setMapSearchQuery('');
    }
    setMapPickerOpen(true);
  };

  // Keep form lat/lng in sync with selected location while the picker is open
  useEffect(() => {
    if (mapPickerOpen && selectedMapLocation) {
      setFormData(prev => ({
        ...prev,
        latitude: selectedMapLocation.lat.toString(),
        longitude: selectedMapLocation.lng.toString(),
      }));
    }
  }, [selectedMapLocation, mapPickerOpen]);

  const loadGoogleMaps = () => {
    if (window.google?.maps) return Promise.resolve();
    if (googleMapsLoading.current) return googleMapsLoading.current;
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyB1EiZd7oGK4fnmgbeOXyFmWOeCoJmKrAc';
    googleMapsLoading.current = new Promise<void>((resolve, reject) => {
      const callbackName = `gmapsInit_${Date.now()}`;
      (window as any)[callbackName] = () => {
        delete (window as any)[callbackName];
        resolve();
      };
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&callback=${callbackName}`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        reject(new Error('Failed to load Google Maps script'));
      };
      document.head.appendChild(script);
    });
    return googleMapsLoading.current;
  };

  // Initialize map when picker opens
  useEffect(() => {
    if (!mapPickerOpen) {
      // Cleanup: destroy map instance when dialog closes
      if (mapInstanceRef.current) {
        // Clear all listeners
        if (markerRef.current) {
          window.google?.maps?.event?.clearInstanceListeners?.(markerRef.current);
          markerRef.current = null;
        }
        mapInstanceRef.current = null;
      }
      // Cleanup autocomplete
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
        autocompleteRef.current = null;
      }
      setAutocompleteWidgetReady(false);
      return;
    }

    // Wait a bit for the dialog to fully render and container to be available
    const initMap = async () => {
      try {
        await loadGoogleMaps();
        
        // Wait for container to be ready
        let retries = 0;
        while (!mapContainerRef.current && retries < 10) {
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
        }
        
        if (!mapContainerRef.current) {
          console.error('Map container not found');
          return;
        }

        // Destroy existing map if any
        if (mapInstanceRef.current) {
          if (markerRef.current) {
            window.google?.maps?.event?.clearInstanceListeners?.(markerRef.current);
            markerRef.current = null;
          }
          mapInstanceRef.current = null;
        }

        const defaultPos = selectedMapLocation || { lat: 29.3759, lng: 47.9774 };
        
        try {
          mapInstanceRef.current = new window.google.maps.Map(mapContainerRef.current, {
            center: defaultPos,
            zoom: 12,
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: true,
          });
        } catch (mapErr: any) {
          // Handle map initialization errors
          const errorMsg = mapErr?.message || String(mapErr);
          if (errorMsg.includes('ApiTargetBlocked') || errorMsg.includes('RefererNotAllowed') || errorMsg.includes('ApiTargetBlockedMapError')) {
            throw new Error('Google Maps API key is restricted. Please configure it in Google Cloud Console to allow localhost:3000');
          }
          throw mapErr;
        }
        
        // Listen for map errors
        if (mapInstanceRef.current) {
          // Google Maps doesn't have a direct error event, but we can catch errors during operations
          // The error will be caught in the outer try-catch
        }
        
        markerRef.current = new window.google.maps.Marker({
          position: defaultPos,
          map: mapInstanceRef.current,
          draggable: true,
        });
        
        // Trigger resize after a short delay to ensure map renders correctly
        setTimeout(() => {
          if (mapInstanceRef.current) {
            window.google?.maps?.event?.trigger(mapInstanceRef.current, 'resize');
          }
          
          // Check if map actually rendered (ApiTargetBlocked errors prevent rendering)
          if (mapContainerRef.current && mapContainerRef.current.children.length === 0) {
            // Map didn't render - likely an API key restriction error
            setMapError('Google Maps failed to load. Please check your API key configuration in Google Cloud Console. Ensure localhost:3000 is allowed.');
          }
        }, 1000);
        
        // Drag updates
        markerRef.current.addListener('dragend', (e: any) => {
          const pos = e.latLng;
          if (pos) {
            setSelectedMapLocation({ lat: pos.lat(), lng: pos.lng() });
          }
        });
        
        // Click on map updates
        mapInstanceRef.current.addListener('click', (e: any) => {
          if (e.latLng) {
            const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
            setSelectedMapLocation(pos);
          }
        });

        // Initialize Autocomplete widget - wait for input to be ready
        const initAutocomplete = () => {
          if (!autocompleteInputRef.current) {
            // Wait a bit more for the input to be ready
            setTimeout(initAutocomplete, 100);
            return;
          }

          if (!window.google?.maps?.places?.Autocomplete) {
            console.warn('Google Maps Places Autocomplete not available');
            return;
          }

          try {
            // Destroy existing autocomplete if any
            if (autocompleteRef.current) {
              window.google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
              autocompleteRef.current = null;
            }

            const autocompleteOptions: any = {
              fields: ['place_id', 'geometry', 'formatted_address', 'name'],
              types: ['establishment', 'geocode'], // Allow both businesses and addresses
            };

            // Add location bias if we have a selected location
            if (selectedMapLocation) {
              autocompleteOptions.locationBias = new window.google.maps.Circle({
                center: selectedMapLocation,
                radius: 50000, // 50km
              });
            }

            autocompleteRef.current = new window.google.maps.places.Autocomplete(
              autocompleteInputRef.current,
              autocompleteOptions
            );

            // Handle place selection
            autocompleteRef.current.addListener('place_changed', () => {
              const place = autocompleteRef.current.getPlace();
              if (place.geometry?.location) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                setSelectedMapLocation({ lat, lng });
                setMapSearchQuery(place.formatted_address || place.name || '');
                setMapPredictions([]);
              }
            });
            
            setAutocompleteWidgetReady(true);
            console.log('Google Maps Autocomplete widget initialized successfully');
          } catch (autocompleteErr) {
            console.warn('Autocomplete widget initialization failed, will use manual search', autocompleteErr);
            setAutocompleteWidgetReady(false);
            // Continue without autocomplete widget - user can still search manually
          }
        };

        // Initialize autocomplete after a short delay to ensure input is ready
        setTimeout(initAutocomplete, 200);
        
        setMapError(null);
      } catch (err: any) {
        console.error('Google Maps load failed', err);
        const errorMessage = err?.message || 'Failed to load Google Maps';
        setMapError(errorMessage);
        
        // Show user-friendly error
        if (errorMessage.includes('restricted') || errorMessage.includes('ApiTargetBlocked') || errorMessage.includes('RefererNotAllowed')) {
          setMapError('Google Maps API key is restricted. Please configure it in Google Cloud Console to allow localhost:3000');
        }
      }
    };

    initMap();

    // Cleanup autocomplete on unmount
    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [mapPickerOpen]);

  // Keep marker and map center in sync when selected location changes
  useEffect(() => {
    if (mapInstanceRef.current && selectedMapLocation) {
      mapInstanceRef.current.panTo(selectedMapLocation);
      if (markerRef.current) {
        markerRef.current.setPosition(selectedMapLocation);
      }
    }
  }, [selectedMapLocation]);

  // Fetch autocomplete predictions as fallback (when Autocomplete widget is not available or fails)
  useEffect(() => {
    const controller = new AbortController();
    
    // Only use fetch-based autocomplete if the widget is not available or not ready
    const useFetchAutocomplete = !autocompleteWidgetReady || !window.google?.maps?.places?.Autocomplete;
    
    if (!useFetchAutocomplete) {
      // Autocomplete widget is working, don't use fetch
      return;
    }
    
    const run = async () => {
      const query = mapSearchQuery.trim();
      if (!query) {
        setMapPredictions([]);
        setIsSearchingPlaces(false);
        return;
      }
      
      // Don't search for coordinates
      if (query.match(/^-?\d+\.?\d*\s*,\s*-?\d+\.?\d*$/)) {
        setMapPredictions([]);
        setIsSearchingPlaces(false);
        return;
      }
      
      setIsSearchingPlaces(true);
      try {
        const googleApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyB1EiZd7oGK4fnmgbeOXyFmWOeCoJmKrAc';
        let predictions: { description: string; place_id?: string; lat?: number; lng?: number }[] = [];
        const bias = selectedMapLocation
          ? `&locationbias=point:${selectedMapLocation.lat},${selectedMapLocation.lng}`
          : '';

        // Try Google Places autocomplete via backend proxy (to avoid CORS)
        // For now, fallback to OSM
        try {
          const osmResp = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=10&addressdetails=1`,
            { signal: controller.signal, headers: { 'User-Agent': 'Smart Building Management System' } }
          );
          if (osmResp.ok) {
            const osmData = await osmResp.json();
            predictions = (osmData || []).map((p: any) => ({
              description: p.display_name,
              lat: parseFloat(p.lat),
              lng: parseFloat(p.lon),
            }));
          }
        } catch (err) {
          if ((err as any)?.name !== 'AbortError') {
            console.error('OSM autocomplete failed', err);
          }
        }

        setMapPredictions(predictions);
      } catch {
        setMapPredictions([]);
      } finally {
        setIsSearchingPlaces(false);
      }
    };
    
    const t = setTimeout(run, 300);
    return () => {
      clearTimeout(t);
      controller.abort();
      setIsSearchingPlaces(false);
    };
  }, [mapSearchQuery, selectedMapLocation]);

  const handlePredictionSelect = async (prediction: { description: string; place_id?: string; lat?: number; lng?: number }) => {
    setMapSearchQuery(prediction.description);
    setMapPredictions([]);
    // If we already have lat/lng (OSM), use directly
    if (prediction.lat && prediction.lng) {
      setSelectedMapLocation({ lat: prediction.lat, lng: prediction.lng });
      return;
    }
    // Else fetch Google place details
    if (prediction.place_id) {
      try {
        const googleApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyB1EiZd7oGK4fnmgbeOXyFmWOeCoJmKrAc';
        const resp = await fetch(
          `https://maps.googleapis.com/maps/api/place/details/json?place_id=${prediction.place_id}&fields=geometry&key=${googleApiKey}`
        );
        const data = await resp.json();
        const loc = data?.result?.geometry?.location;
        if (loc?.lat && loc?.lng) {
          setSelectedMapLocation({ lat: loc.lat, lng: loc.lng });
        }
      } catch (err) {
        console.error('Place details failed', err);
      }
    }
  };

  const clearPredictions = () => {
    setMapPredictions([]);
    setIsSearchingPlaces(false);
  };

  const handleMapSearch = async () => {
    if (!mapSearchQuery.trim()) return;
    
    // Check if user entered coordinates directly (e.g., "29.3759, 47.9774")
    const coordMatch = mapSearchQuery.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]);
      const lng = parseFloat(coordMatch[2]);
      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        setSelectedMapLocation({ lat, lng });
        return;
      }
    }
    
    try {
      const googleApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyB1EiZd7oGK4fnmgbeOXyFmWOeCoJmKrAc';

      // First: Google Places Find Place (text)
      try {
        const placeResp = await fetch(
          `https://maps.googleapis.com/maps/api/place/findplacefromtext/json?input=${encodeURIComponent(mapSearchQuery)}&inputtype=textquery&fields=geometry,name,formatted_address&key=${googleApiKey}`
        );
        const placeData = await placeResp.json();
        if (placeData?.candidates?.length) {
          const loc = placeData.candidates[0].geometry?.location;
          if (loc?.lat && loc?.lng) {
            setSelectedMapLocation({ lat: loc.lat, lng: loc.lng });
            return;
          }
        }
      } catch (err) {
        console.error('Google Places lookup failed', err);
      }

      // Second: Google Geocoding
      try {
        const googleResponse = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(mapSearchQuery)}&key=${googleApiKey}`
        );
        const googleData = await googleResponse.json();
        
        if (googleData.results && googleData.results.length > 0) {
          const location = googleData.results[0].geometry.location;
          setSelectedMapLocation({
            lat: location.lat,
            lng: location.lng,
          });
          return;
        }
      } catch (err) {
        console.error('Google geocode failed', err);
      }

      // Fallback: OpenStreetMap Nominatim
      const osmResponse = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(mapSearchQuery)}&limit=1&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'Smart Building Management System'
          }
        }
      );
      
      if (osmResponse.ok) {
        const osmData = await osmResponse.json();
        if (osmData && osmData.length > 0) {
          const result = osmData[0];
          setSelectedMapLocation({
            lat: parseFloat(result.lat),
            lng: parseFloat(result.lon),
          });
        } else {
          alert(t('buildings.mapDialog.locationNotFoundSimple'));
        }
      } else {
        alert(t('buildings.mapDialog.locationNotFoundSimple'));
      }
    } catch (error) {
      console.error('Error searching location:', error);
      alert(t('buildings.mapDialog.locationSearchError'));
    }
  };

  const handleConfirmLocation = () => {
    if (selectedMapLocation) {
      setFormData(prev => ({
        ...prev,
        latitude: selectedMapLocation.lat.toString(),
        longitude: selectedMapLocation.lng.toString(),
      }));
      setMapPickerOpen(false);
      setSelectedMapLocation(null);
      setMapSearchQuery('');
    } else {
      alert(t('buildings.mapDialog.selectFirst'));
    }
  };


  const handleDelete = async (id: number) => {
    if (window.confirm(t('buildings.messages.delete.confirm'))) {
      try {
        await api.delete(`/buildings/${id}`);
        fetchBuildings();
        notifySuccess(t('buildings.messages.delete.success'));
      } catch (error) {
        console.error('Error deleting building:', error);
        const errorMessage = extractAndTranslateError(error, language, t);
        notifyError(errorMessage || t('buildings.messages.delete.errorDefault'));
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    const numericFields = ['total_floors', 'total_flats'];

    // Prevent negative numbers for numeric fields; allow empty while typing
    if (type === 'number' && numericFields.includes(name)) {
      const sanitized = value === '' ? '' : Math.max(0, Number(value));
      setFormData(prev => ({ ...prev, [name]: sanitized }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return <div>{t('buildings.loading')}</div>;
  }

  const filteredBuildings = buildings.filter((b) => {
    const term = searchTerm.toLowerCase();
    return (
      b.name.toLowerCase().includes(term) ||
      (b.paci_number || '').toLowerCase().includes(term) ||
      (b.building_code || '').toLowerCase().includes(term)
    );
  });

  const handleExportExcel = async () => {
    if (!filteredBuildings.length) {
      notifyError(t('buildings.export.noData'));
      return;
    }
    const rows = filteredBuildings.map((b) => ({
      [t('buildings.export.columns.name')]: language === 'ar' && b.name_ar ? b.name_ar : b.name,
      [t('buildings.export.columns.code')]: b.building_code,
      [t('buildings.export.columns.paci')]: b.paci_number || '',
      [t('buildings.export.columns.flats')]: b.total_flats,
      [t('buildings.export.columns.floors')]: b.total_floors,
      [t('buildings.export.columns.securityGuardPhone')]: b.haris_phone_number || '',
      [t('buildings.export.columns.active')]: b.is_active ? (language === 'ar' ? 'نعم' : 'Yes') : (language === 'ar' ? 'لا' : 'No'),
    }));
    await exportToExcel(rows, 'buildings');
    notifySuccess(t('buildings.export.excel.success'));
  };

  const handleExportPDF = async () => {
    if (!filteredBuildings.length) {
      notifyError(t('buildings.export.noData'));
      return;
    }
    const columns = [
      t('buildings.export.columns.name'),
      t('buildings.export.columns.code'),
      t('buildings.export.columns.paci'),
      t('buildings.export.columns.flats'),
      t('buildings.export.columns.floors'),
      t('buildings.export.columns.securityGuardPhone'),
      t('buildings.export.columns.active')
    ];
    const data = filteredBuildings.map((b) => [
      language === 'ar' && b.name_ar ? b.name_ar : b.name,
      b.building_code,
      b.paci_number || '',
      b.total_flats,
      b.total_floors,
      b.haris_phone_number || '',
      b.is_active ? (language === 'ar' ? 'نعم' : 'Yes') : (language === 'ar' ? 'لا' : 'No'),
    ]);
    await exportToPDF(columns, data, 'buildings', language, t('buildings.title'));
    notifySuccess(t('buildings.export.pdf.success'));
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={1} flexWrap="wrap">
        <Typography variant="h4">{t('buildings.title')}</Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder={t('buildings.search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ minWidth: 260 }}
          />
          {can('buildings.export-excel') && (
            <Button variant="outlined" onClick={handleExportExcel}>
              {t('buildings.export.excel')}
            </Button>
          )}
          {can('buildings.export-pdf') && (
            <Button variant="outlined" onClick={handleExportPDF}>
              {t('buildings.export.pdf')}
            </Button>
          )}
          {can('buildings.add') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpen()}
            >
              {t('buildings.dialog.addTitle')}
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {filteredBuildings.map((building) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={building.id}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                minHeight: 300,
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }}>
                    {language === 'ar' && building.name_ar ? building.name_ar : building.name}
                  </Typography>
                  <Box>
                    {can('buildings.edit') && (
                      <IconButton
                        size="small"
                        onClick={() => handleOpen(building)}
                      >
                        <Edit />
                      </IconButton>
                    )}
                    {can('buildings.delete') && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(building.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('buildings.card.address')}
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <LocationOn fontSize="small" color="action" sx={{ mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {building.address}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      {t('buildings.card.buildingCode')}
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <Business fontSize="small" color="action" sx={{ mr: 0.5 }} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatStringWithNumbers(building.building_code, language)}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip
                      label={`${formatNumber(building.total_floors, language)} ${t('buildings.card.floors')}`}
                      size="small"
                      color="primary"
                    />
                    <Chip
                      label={`${formatNumber(building.total_flats, language)} ${t('buildings.card.flats')}`}
                      size="small"
                      color="secondary"
                    />
                  </Box>
                  
                  {building.paci_number && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {t('buildings.card.paciNumber')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {formatStringWithNumbers(building.paci_number, language)}
                      </Typography>
                    </Box>
                  )}
                  
                  {building.haris_phone_number && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {t('buildings.card.harisPhone')}
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Phone fontSize="small" color="action" sx={{ mr: 0.5 }} />
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                          {formatPhoneNumber(building.haris_phone_number, language)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  {building.amenities && building.amenities.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {t('buildings.card.amenities')}
                      </Typography>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {building.amenities.map((amenityId) => {
                          const amenity = amenities.find(a => Number(a.id) === Number(amenityId));
                          return amenity ? (
                            <Chip
                              key={amenityId}
                              label={language === 'ar' ? amenity.name_ar : amenity.name_en}
                              size="small"
                              variant="outlined"
                            />
                          ) : null;
                        })}
                      </Box>
                    </Box>
                  )}
                  
                  {/* Maintenance fund removed as per latest requirements */}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingBuilding ? 'Edit Building' : 'Add New Building'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('buildings.dialog.name')}
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
                <TextField
                  fullWidth
                  label={t('buildings.dialog.nameAr')}
                  name="name_ar"
                  value={formData.name_ar}
                  onChange={handleInputChange}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('buildings.dialog.code')}
                  name="building_code"
                  value={formData.building_code}
                  onChange={handleInputChange}
                />
              </Box>
              
              <TextField
                fullWidth
                label={t('buildings.dialog.address')}
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                multiline
                rows={2}
                required
              />
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Button
                  variant="outlined"
                  startIcon={<LocationOn />}
                  onClick={handleMapPickerOpen}
                  sx={{ mt: 1 }}
                >
                  {t('buildings.dialog.chooseLocation')}
                </Button>
                {(formData.latitude && formData.longitude) && (
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('buildings.dialog.selectedPrefix')} {parseFloat(formData.latitude).toFixed(6)}, {parseFloat(formData.longitude).toFixed(6)}
                    </Typography>
                    <Box sx={{ mt: 1, width: '100%', height: 200, borderRadius: 2, overflow: 'hidden' }}>
                      <iframe
                        title="Selected Location Preview"
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        src={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}&z=15&output=embed`}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </Box>
                  </Box>
                )}
              </Box>
              
              <TextField
                fullWidth
                label={t('buildings.dialog.paciNumber')}
                name="paci_number"
                value={formData.paci_number}
                onChange={handleInputChange}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('buildings.dialog.totalFloors')}
                  name="total_floors"
                  type="number"
                  value={formData.total_floors}
                  onChange={handleInputChange}
                  required
                  inputProps={{ min: 0 }}
                />
                <TextField
                  fullWidth
                  label={t('buildings.dialog.totalFlats')}
                  name="total_flats"
                  type="number"
                  value={formData.total_flats}
                  onChange={handleInputChange}
                  required
                  inputProps={{ min: 0 }}
                />
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('buildings.dialog.harisPhone')}
                  name="haris_phone_number"
                  value={formData.haris_phone_number}
                  onChange={handleInputChange}
                />
              </Box>
              
              <TextField
                fullWidth
                label={t('buildings.dialog.description')}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <FormControl fullWidth>
                  <InputLabel>{t('buildings.dialog.amenities')}</InputLabel>
                  <Select
                    multiple
                    value={formData.amenities}
                    label={t('buildings.dialog.amenities')}
                    onChange={(e) => {
                      const value = e.target.value;
                      const ids = typeof value === 'string' 
                        ? value.split(',').map(v => Number(v)).filter(id => !isNaN(id))
                        : (value as number[]).map(id => Number(id)).filter(id => !isNaN(id));
                      setFormData({ ...formData, amenities: ids });
                    }}
                    renderValue={(selected) => {
                      const selectedIds = Array.isArray(selected) ? selected.map(id => Number(id)) : [];
                      const selectedAmenities = amenities.filter(a => selectedIds.includes(Number(a.id)));
                      return selectedAmenities.map(a => language === 'ar' ? a.name_ar : a.name_en).join(', ');
                    }}
                  >
                    {amenities.map((amenity) => (
                      <MenuItem 
                        key={amenity.id} 
                        value={amenity.id}
                      >
                        <Checkbox 
                          checked={formData.amenities.map(id => Number(id)).includes(Number(amenity.id))}
                          tabIndex={-1}
                          disableRipple
                        />
                        <ListItemText 
                          primary={language === 'ar' ? amenity.name_ar : amenity.name_en}
                        />
                        {can('buildings.edit') && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setEditingAmenity(amenity);
                              setNewAmenity({
                                name_en: amenity.name_en,
                                name_ar: amenity.name_ar,
                              });
                              setAmenityDialogOpen(true);
                            }}
                            sx={{ ml: 1 }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {can('buildings.edit') && (
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => {
                      setEditingAmenity(null);
                      setNewAmenity({ name_en: '', name_ar: '' });
                      setAmenityDialogOpen(true);
                    }}
                    sx={{ minWidth: 120 }}
                  >
                    {t('buildings.dialog.addAmenity')}
                  </Button>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={submitting}>{t('buildings.dialog.cancel')}</Button>
            <Button type="submit" variant="contained" disabled={submitting} startIcon={submitting ? <CircularProgress size={20} /> : null}>
              {editingBuilding ? t('buildings.dialog.update') : t('buildings.dialog.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Add/Edit Amenity Dialog */}
      <Dialog open={amenityDialogOpen} onClose={() => {
        setAmenityDialogOpen(false);
        setEditingAmenity(null);
        setNewAmenity({ name_en: '', name_ar: '' });
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAmenity ? t('buildings.dialog.editAmenity.title') : t('buildings.dialog.addAmenity.title')}
        </DialogTitle>
        <form onSubmit={async (e) => {
          e.preventDefault();
          if (submittingAmenity) return; // Prevent double submission
          setSubmittingAmenity(true);
          try {
            if (editingAmenity) {
              await api.put(`/amenities/${editingAmenity.id}`, newAmenity);
              notifySuccess(t('buildings.dialog.editAmenity.success'));
            } else {
              await api.post('/amenities', newAmenity);
              notifySuccess(t('buildings.dialog.addAmenity.success'));
            }
            setNewAmenity({ name_en: '', name_ar: '' });
            setEditingAmenity(null);
            setAmenityDialogOpen(false);
            fetchAmenities();
          } catch (error: any) {
            console.error(`Error ${editingAmenity ? 'updating' : 'adding'} amenity:`, error);
            const errorMessage = extractAndTranslateError(error, language, t);
            notifyError(errorMessage || (editingAmenity ? t('buildings.dialog.editAmenity.error') : t('buildings.dialog.addAmenity.error')));
          } finally {
            setSubmittingAmenity(false);
          }
        }}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                fullWidth
                label={t('buildings.dialog.addAmenity.nameEn')}
                value={newAmenity.name_en}
                onChange={(e) => setNewAmenity({ ...newAmenity, name_en: e.target.value })}
                required
              />
              <TextField
                fullWidth
                label={t('buildings.dialog.addAmenity.nameAr')}
                value={newAmenity.name_ar}
                onChange={(e) => setNewAmenity({ ...newAmenity, name_ar: e.target.value })}
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setAmenityDialogOpen(false);
              setEditingAmenity(null);
              setNewAmenity({ name_en: '', name_ar: '' });
            }} disabled={submittingAmenity}>
              {t('buildings.dialog.cancel')}
            </Button>
            <Button type="submit" variant="contained" disabled={submittingAmenity} startIcon={submittingAmenity ? <CircularProgress size={20} /> : null}>
              {editingAmenity ? t('buildings.dialog.editAmenity.update') : t('buildings.dialog.addAmenity.add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Map Picker Dialog */}
      <Dialog open={mapPickerOpen} onClose={() => {
        setMapPickerOpen(false);
        setSelectedMapLocation(null);
        setMapSearchQuery('');
        setMapPredictions([]);
        setIsSearchingPlaces(false);
        setMapError(null);
      }} maxWidth="lg" fullWidth>
        <DialogTitle>{t('buildings.mapDialog.title')}</DialogTitle>
        <DialogContent>
          <Box
            sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 1, minHeight: 520 }}
            onClick={clearPredictions}
          >
            {/* Left: Search and suggestions */}
            <Box sx={{ width: 320, minWidth: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1, position: 'relative' }} onClick={(e) => e.stopPropagation()}>
              <Typography variant="body2" color="text.secondary">
                {t('buildings.mapDialog.helpText')}
              </Typography>
              <Box sx={{ position: 'relative', zIndex: 1300 }}>
                <TextField
                  inputRef={autocompleteInputRef}
                  fullWidth
                  size="small"
                  label={t('buildings.mapDialog.searchLabel')}
                  value={mapSearchQuery}
                  onChange={(e) => setMapSearchQuery(e.target.value)}
                  placeholder={t('buildings.mapDialog.searchPlaceholder')}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleMapSearch();
                    }
                  }}
                  helperText={t('buildings.mapDialog.searchHelper')}
                  InputProps={{
                    endAdornment: isSearchingPlaces ? (
                      <InputAdornment position="end">
                        <CircularProgress size={20} sx={{ color: 'red' }} />
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    '& .pac-container': {
                      zIndex: 1400, // Ensure autocomplete dropdown appears above everything
                      marginTop: '4px',
                    },
                  }}
                />
              </Box>
              {/* Custom dropdown for OSM fallback results (only shown if Google Autocomplete widget is not working) */}
              {/* Google Maps Autocomplete widget creates its own dropdown automatically when working */}
              {(!autocompleteWidgetReady || mapPredictions.length > 0 || isSearchingPlaces) && (
                <Box
                  sx={{
                    minHeight: 200,
                    maxHeight: 400,
                    height: mapPredictions.length > 0 || isSearchingPlaces ? 'auto' : 200,
                    overflowY: mapPredictions.length > 0 || isSearchingPlaces ? 'auto' : 'hidden',
                    overflowX: 'hidden',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    boxShadow: 2,
                    position: 'relative',
                    zIndex: 1300,
                    visibility: 'visible',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {isSearchingPlaces ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3, flex: 1 }}>
                      <CircularProgress size={24} sx={{ color: 'red' }} />
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        {t('buildings.mapDialog.searching') || 'Searching places...'}
                      </Typography>
                    </Box>
                  ) : mapPredictions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
                      {mapSearchQuery.trim() ? (t('buildings.mapDialog.noSuggestions') || 'No suggestions found') : (t('buildings.mapDialog.startTyping') || 'Start typing to search for locations...')}
                    </Typography>
                  ) : (
                    mapPredictions.map((p, idx) => (
                      <Box
                        key={`${p.place_id || p.description}-${idx}`}
                        sx={{
                          px: 1.5,
                          py: 1.25,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          borderBottom: idx === mapPredictions.length - 1 ? 'none' : '1px solid',
                          borderColor: 'divider',
                        }}
                        onClick={() => handlePredictionSelect(p)}
                      >
                        <Typography variant="body2">{p.description}</Typography>
                      </Box>
                    ))
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label={t('buildings.mapDialog.latitude')}
                  value={selectedMapLocation?.lat.toFixed(6) || ''}
                  onChange={(e) => {
                    const lat = parseFloat(e.target.value);
                    if (!isNaN(lat)) {
                      if (selectedMapLocation) {
                        setSelectedMapLocation({ ...selectedMapLocation, lat });
                      } else {
                        setSelectedMapLocation({ lat, lng: 47.9774 }); // fallback lng
                      }
                    }
                  }}
                  type="number"
                  inputProps={{ step: "0.000001" }}
                  placeholder={t('buildings.mapDialog.latPlaceholder')}
                  size="small"
                />
                <TextField
                  fullWidth
                  label={t('buildings.mapDialog.longitude')}
                  value={selectedMapLocation?.lng.toFixed(6) || ''}
                  onChange={(e) => {
                    const lng = parseFloat(e.target.value);
                    if (!isNaN(lng)) {
                      if (selectedMapLocation) {
                        setSelectedMapLocation({ ...selectedMapLocation, lng });
                      } else {
                        setSelectedMapLocation({ lat: 29.3759, lng }); // fallback lat
                      }
                    }
                  }}
                  type="number"
                  inputProps={{ step: "0.000001" }}
                  placeholder={t('buildings.mapDialog.lngPlaceholder')}
                  size="small"
                />
              </Box>
              {selectedMapLocation && (
                <Typography variant="body2" color="text.secondary">
                  {/* Selected: {selectedMapLocation.lat.toFixed(6)}, {selectedMapLocation.lng.toFixed(6)} */}
                </Typography>
              )}
            </Box>

            {/* Right: Map */}
            <Box sx={{ flex: 1, minHeight: 520, position: 'relative', borderRadius: 2, overflow: 'hidden', boxShadow: 3, bgcolor: 'grey.100' }} onClick={(e) => e.stopPropagation()}>
              {mapError ? (
                <Box sx={{ 
                  position: 'absolute', 
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  bgcolor: 'error.light',
                  color: 'error.contrastText',
                  textAlign: 'center'
                }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
                    Google Maps Error
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {mapError}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                    You can still enter coordinates manually using the latitude and longitude fields below.
                  </Typography>
                </Box>
              ) : (
                <Box 
                  ref={mapContainerRef} 
                  sx={{ 
                    position: 'absolute', 
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    minHeight: 520
                  }} 
                />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMapPickerOpen(false);
            setSelectedMapLocation(null);
            setMapSearchQuery('');
            clearPredictions();
          }}>{t('buildings.mapDialog.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => { clearPredictions(); handleConfirmLocation(); }}
            disabled={!selectedMapLocation}
          >
            {t('buildings.mapDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Buildings;